import { CourseTemplate as CourseTemplateEntity } from '@dosfilos/domain';
/**
 * Compose a syllabus by merging the layers. Callers pass the entities
 * after resolving them through their respective repositories. The composer
 * never reaches into a repository itself — keeps this pure + testable.
 *
 * Layer precedence (highest to lowest):
 *   - Course            (academic identity — description, OACs, anchorVerse, biblicalVersion)
 *   - Template          (execution — modules, assignments, grading, readings, sessionFormat)
 *   - Program           (program-level — OAPs, philosophy, latePolicy override)
 *   - Department        (department-level — OADs, dept purpose, color fallback)
 *   - Tenant            (institutional — mission, default rubric, default policies)
 *
 * Phase C: Course is the academic-identity source of truth. Falls back to
 * template fields when Course hasn't been migrated yet (gradual migration).
 */
export function composeSyllabus(template, ctx) {
    const tenantConfig = ctx.tenant?.config;
    const programSnap = ctx.program?.['toSnapshot']?.() ?? null;
    const deptSnap = ctx.department?.['toSnapshot']?.() ?? null;
    const courseSnap = ctx.course?.['toSnapshot']?.() ?? null;
    // Policies = institutional (tenant) + operational (program). Programs
    // can override a tenant policy by using the SAME title.
    const tenantPolicies = tenantConfig?.policies ?? [];
    const programPolicies = programSnap?.policies ?? [];
    const policiesByTitle = new Map();
    for (const p of tenantPolicies)
        policiesByTitle.set(p.title.toLowerCase(), p);
    for (const p of programPolicies)
        policiesByTitle.set(p.title.toLowerCase(), p);
    const composedPolicies = Array.from(policiesByTitle.values());
    // Research rubric: program override beats tenant default.
    const composedRubric = programSnap?.researchRubric ?? tenantConfig?.researchRubric;
    // Phase C.1: build the catalog visible to this template, then narrow to
    // only the rubrics actually referenced by the template's assignments.
    const programRubricsCatalog = (programSnap?.rubrics && programSnap.rubrics.length > 0
        ? programSnap.rubrics
        : programSnap?.researchRubric
            ? [programSnap.researchRubric]
            : []) ?? [];
    const tenantRubricsCatalog = (tenantConfig?.rubrics && tenantConfig.rubrics.length > 0
        ? tenantConfig.rubrics
        : tenantConfig?.researchRubric
            ? [tenantConfig.researchRubric]
            : []) ?? [];
    // Program catalog wins over tenant catalog when both have entries; per-id
    // override happens via the lookup below (program's id beats tenant's same id).
    const rubricById = new Map();
    for (const r of tenantRubricsCatalog)
        rubricById.set(r.id, r);
    for (const r of programRubricsCatalog)
        rubricById.set(r.id, r);
    // Template snapshot (it has its own getters but we need raw props).
    const t = template.toSnapshot();
    // Phase C.1: collect rubricRef ids from this template's assignments (dedupe).
    // Resolve each against the program/tenant catalog. Unknown ids drop silently
    // (renderer treats absence as "no rubric"; UI surfaces the dangling ref
    // before save).
    const referencedRubrics = (() => {
        const refIds = [];
        for (const a of t.assignments ?? []) {
            const rid = a.rubricRef;
            if (rid && !refIds.includes(rid))
                refIds.push(rid);
        }
        const resolved = [];
        for (const rid of refIds) {
            const r = rubricById.get(rid);
            if (r)
                resolved.push(r);
        }
        return resolved;
    })();
    // Phase C: Course owns academic identity. Wrap plain-text Course.description
    // into a minimal TipTap doc so the renderer's shape stays stable. Fall back
    // to legacy template.description if Course not migrated.
    const courseDescription = courseSnap?.description && courseSnap.description.trim()
        ? {
            type: 'doc',
            content: courseSnap.description.split(/\n{2,}/).map((para) => ({
                type: 'paragraph',
                content: [{ type: 'text', text: para }],
            })),
        }
        : t.description;
    const courseObjectives = courseSnap?.learningObjectives?.length
        ? courseSnap.learningObjectives
        : t.objectives.course;
    return {
        templateId: t.id,
        title: t.title,
        code: t.code,
        version: t.version,
        programId: t.programId,
        departmentId: programSnap?.departmentId,
        mission: tenantConfig?.mission
            ? {
                text: tenantConfig.mission.text,
                ...(tenantConfig.mission.scripture !== undefined
                    ? { scripture: tenantConfig.mission.scripture }
                    : {}),
            }
            : undefined,
        programName: programSnap?.name,
        programPurpose: programSnap?.purpose,
        programObjectives: programSnap?.learningObjectives ?? [],
        // Phase B: prefer department entity. Falls back to legacy program-level
        // fields if the program hasn't been migrated yet.
        departmentName: deptSnap?.name ?? programSnap?.departmentName,
        departmentPurpose: deptSnap?.purpose ?? programSnap?.departmentPurpose,
        departmentObjectives: deptSnap?.learningObjectives ?? programSnap?.departmentObjectives ?? [],
        philosophy: programSnap?.philosophy ?? [],
        policies: composedPolicies,
        latePolicy: programSnap?.latePolicy ?? t.latePolicy,
        modality: programSnap?.modality,
        technologyRequirements: programSnap?.technologyRequirements,
        researchRubric: composedRubric,
        referencedRubrics,
        description: courseDescription,
        courseObjectives,
        grading: t.grading,
        readings: t.readings,
        sessionFormat: t.sessionFormat,
        modules: t.modules,
        assignments: t.assignments,
        anchorVerse: courseSnap?.anchorVerse ?? t.anchorVerse,
        biblicalVersion: courseSnap?.biblicalVersion ?? t.biblicalVersion,
    };
}
// ─── Section content overrides (F2) ─────────────────────────────────────────
//
// A teacher edits the syllabus of ONE section from /curso; each edit is stored
// as a Syllabus.overrides entry (path → value) — never a copy of the template.
// Effective content = section override ?? composed ?? template. This helper
// projects the whitelisted overrides onto a CourseTemplate so every consumer
// that renders from the template (canonical pages, late-policy freeze) sees
// the section's content without new plumbing.
export const SYLLABUS_CONTENT_OVERRIDE_PATHS = [
    'description',
    'objectives.course',
    'grading.note',
    'readings',
    'policiesNote',
    'latePolicy',
    // The section's WHOLE evaluation table (names, weights, hours). Listed before
    // `grading.descriptions` on purpose: overrides are applied in THIS order, and
    // the descriptions patch the table the section chose, not the template's.
    'grading.components',
    // "Detalles de la tarea": the intro prose and each evaluation's description.
    'assignmentsNote',
    'grading.descriptions',
    // Whether the course calendar lists the weekly readings (director, 2026-09-30:
    // Teología IV keeps only the deliveries there).
    'calendar.showReadings',
];
export function isSyllabusContentOverridePath(path) {
    return SYLLABUS_CONTENT_OVERRIDE_PATHS.includes(path);
}
function withDescriptions(components, value) {
    if (!components || !value || typeof value !== 'object')
        return components;
    const byId = value;
    return components.map((c) => {
        if (!(c.id in byId))
            return c;
        const next = byId[c.id];
        if (next === null || next === undefined) {
            const { description: _drop, ...rest } = c;
            return rest;
        }
        return { ...c, description: next };
    });
}
/** The single path→field table, WRITE direction. Used three times on purpose:
 *  to project a section's overrides onto a template for rendering
 *  (applyContentOverrides / applyContentOverrideMap), and to build the patch
 *  that WRITES the template when the academic approves a proposal (F5).
 *  Written once so they can never drift apart. */
export function patchForContentPath(current, path, value) {
    // One cast per branch: the caller's container decides the field types, and a
    // stored override is `unknown` by construction (it round-trips through
    // Firestore). The path→field mapping is what this table guarantees; the value
    // shape is guaranteed by the write-side validation in
    // SetSyllabusContentOverrideUseCase.
    switch (path) {
        case 'description':
            return { description: value };
        case 'objectives.course':
            return { objectives: { ...current.objectives, course: value } };
        case 'grading.note':
            return { grading: { ...current.grading, note: value } };
        case 'readings':
            return { readings: value };
        case 'policiesNote':
            return { policiesNote: value };
        case 'latePolicy':
            return { latePolicy: value };
        case 'assignmentsNote':
            return { assignmentsNote: value };
        case 'grading.components':
            return { grading: { ...current.grading, components: value } };
        case 'grading.descriptions':
            return {
                grading: {
                    ...current.grading,
                    components: withDescriptions(current.grading.components, value),
                },
            };
        case 'calendar.showReadings':
            return { calendarShowsReadings: value };
    }
}
/** The same table, READ direction — what the template holds today for a path.
 *  Lives next to `patchForContentPath` on purpose: a new override path has to
 *  be added to both, and keeping them adjacent makes a half-migration obvious
 *  in review instead of showing up as an empty diff in the proposal inbox. */
export function readContentPath(current, path) {
    switch (path) {
        case 'description':
            return current.description;
        case 'objectives.course':
            return current.objectives.course;
        case 'grading.note':
            return current.grading.note;
        case 'readings':
            return current.readings;
        case 'policiesNote':
            return current.policiesNote;
        case 'latePolicy':
            return current.latePolicy;
        case 'assignmentsNote':
            return current.assignmentsNote;
        case 'grading.components':
            return current.grading.components;
        case 'grading.descriptions':
            return Object.fromEntries((current.grading.components ?? []).map((c) => [c.id, c.description ?? null]));
        case 'calendar.showReadings':
            return current.calendarShowsReadings ?? true;
    }
}
/** Browser-facing form of `applyContentOverrides`: takes the plain
 *  `path → value` map that `getSectionSyllabus` ships as `sectionOverrides`
 *  (already filtered to the whitelist server-side) and returns the effective
 *  template. Non-whitelisted keys are ignored rather than trusted. */
export function applyContentOverrideMap(tpl, overrides) {
    if (!overrides)
        return tpl;
    let next = tpl;
    // CANONICAL order, not storage order: two paths patch `grading.components`.
    for (const path of SYLLABUS_CONTENT_OVERRIDE_PATHS) {
        if (!(path in overrides))
            continue;
        next = { ...next, ...patchForContentPath(next, path, overrides[path]) };
    }
    return next;
}
export function applyContentOverrides(tpl, overrides) {
    const order = (p) => SYLLABUS_CONTENT_OVERRIDE_PATHS.indexOf(p);
    // CANONICAL order, not storage order: two paths patch `grading.components`.
    const relevant = overrides
        .filter((o) => isSyllabusContentOverridePath(o.path))
        .sort((a, b) => order(a.path) - order(b.path));
    if (relevant.length === 0)
        return tpl;
    let next = { ...tpl.toSnapshot() };
    for (const o of relevant) {
        // The filter above is the type guard; TS keeps `path` widened to string
        // across the closure boundary, so narrow it back here.
        const path = o.path;
        next = { ...next, ...patchForContentPath(next, path, o.value) };
    }
    return CourseTemplateEntity.reconstitute(next);
}
// ─── Module paths (F5) ──────────────────────────────────────────────────────
// A teacher can also propose the name/axis of a module they renamed in their
// section. The path carries the TEMPLATE module id (LmsModule.templateModuleId),
// so a manually-created module — which has no counterpart in the template — is
// simply not proposable.
const MODULE_PATH_RE = /^modules\.([A-Za-z0-9_-]+)\.(name|axisTopic)$/;
export function parseModulePath(path) {
    const m = MODULE_PATH_RE.exec(path);
    if (!m)
        return null;
    return { templateModuleId: m[1], field: m[2] };
}
/** Patch that renames one module of the template. Returns null when the module
 *  is no longer in the template (deleted after the proposal was filed) — the
 *  caller turns that into a coded error instead of writing a half-patch. */
export function patchForModulePath(current, path, value) {
    const parsed = parseModulePath(path);
    if (!parsed)
        return null;
    const idx = current.modules.findIndex((m) => m.id === parsed.templateModuleId);
    if (idx === -1)
        return null;
    const modules = [...current.modules];
    modules[idx] = { ...modules[idx], [parsed.field]: String(value ?? '') };
    return { modules };
}
/** True for any path this feature knows how to write onto a template. */
/** Section-only paths: never written onto the template. The evaluation table
 *  is one — on the template the weights live on the template's assignments,
 *  and proposing only the table would leave them out of step. */
export const SECTION_ONLY_OVERRIDE_PATHS = [
    'grading.components',
];
export function isProposablePath(path) {
    if (SECTION_ONLY_OVERRIDE_PATHS.includes(path))
        return false;
    return isSyllabusContentOverridePath(path) || parseModulePath(path) !== null;
}
//# sourceMappingURL=syllabus-composition.js.map